import type { DocumentAttachment } from "@/features/deliveries/types";

export type Priority = "low" | "standard" | "high";
export type RequisitionStatus = "draft" | "submitted" | "approved" | "rejected";
// orders.OrderType — mirrors the backend enum. The design/MDCP kinds are set atomically with
// their stage link, so order_type reliably tells the UI which stage panel (if any) is relevant.
export type OrderType =
  | "standard" | "wpw_terenowe" | "wpw_kompleksowe"
  | "ze_opracowanie" | "ze_kosztorys" | "mdcp_geodezja" | "wody_polskie" | "project_lead";
/** Skąd termin na przyjęcie zamówienia: umowa ramowa albo domyślne 5 dni. */
export type AcceptanceDeadlineSource = "agreement" | "default";

export const ACCEPTANCE_DEADLINE_SOURCE_LABELS: Record<AcceptanceDeadlineSource, string> = {
  agreement: "z umowy ramowej",
  default: "termin domyślny",
};

export type OrderStatus =
  | "sent" | "accepted" | "rejected" | "in_progress"
  | "in_progress_partial" | "submitted_for_acceptance"
  | "accepted_final" | "closed" | "cancelled";

export interface RequisitionLineItem {
  id: string;
  line_item_type: string | null;
  line_item_type_name: string;
  line_item_type_unit: string;
  is_custom: boolean;
  custom_name: string;
  custom_unit: string;
  custom_unit_rate: string | null;
  // Effective framework rate + line value (rate × quantity) the item resolves to once
  // ordered. null when the viewer may not see rates (rate_visibility gating) or, for
  // custom items, when no rate was entered.
  unit_rate: string | null;
  total_value: string | null;
  quantity: string;
  description: string;
  attachment_required: boolean;
  required_file_extension: string;
  result_files_expected: boolean;
}

// Kamień milowy zapotrzebowania — przenoszony na zamówienie przy zatwierdzaniu.
export interface RequisitionMilestone {
  id: string;
  label: string;
  code: string;
  due_date: string;
  sequence: number;
}

export interface Requisition {
  id: string;
  project: string;
  project_name: string;
  reporter: string;
  reporter_name: string;
  contractor: string | null;
  contractor_name: string | null;
  description: string;
  priority: Priority;
  deadline: string | null;
  status: RequisitionStatus;
  line_items: RequisitionLineItem[];
  milestones: RequisitionMilestone[];
  created_at: string;
  updated_at: string;
  order_id: string | null;
  order_number: string | null;
  order_status: OrderStatus | null;
  // WPW order type chosen when raised from the DLC Działki tab; locks the approval order type.
  suggested_order_type: string | null;
  // WPW requisitions only: parcels covered, with the exact owners (one WPW each) and the
  // GK input documents (oświadczenia) that go to the negotiator on approval. [] otherwise.
  wpw_parcels: RequisitionWpwParcel[];
  files_detail: ManagedFileBrief[];
  pock_count: number;
  pock_awaiting_count: number;
  // Czy przedsięwzięcie ma podpięty projekt ZE — nazwa w nagłówku staje się wtedy
  // przejściem do workspace'u DLC_01, tak samo jak na zamówieniu.
  has_ze_project: boolean;
  // Id profilu ZE (DLC_01) — okno zatwierdzania pyta nim o foldery zakładki „Pliki",
  // żeby dało się udostępnić je w tworzonym zamówieniu od razu, bez wracania do projektu.
  ze_project_id: string | null;
  // Kwota minimalna zamówienia z umowy ramowej podwykonawcy zestawiona z wartością
  // zapotrzebowania (te same stawki co wiersz „Razem"). Obecne zawsze, gdy umowa ma
  // minimum; below_minimum = ostrzeżenie. null = umowa bez minimum albo widz nie widzi
  // stawek. Informacja, nie blokada zatwierdzenia.
  min_order_amount_notice: MinOrderAmountNotice | null;
  // Pozycje automatyczne umowy ramowej świadomie pominięte (id LineItemType) — automat
  // nie dopisze ich do zamówienia. Usunięcie wiersza automatycznego w edycji (wyzwalacz
  // zostaje) dopisuje go tu samo; ponowne dodanie wiersza zdejmuje pominięcie.
  declined_auto_line_item_types: string[];
  // Pozycje automatyczne dotyczące TEGO zapotrzebowania (ich wyzwalacz jest na liście) —
  // co dopisze się przy zatwierdzeniu, a co nie i dlaczego. Tylko w szczególe (null w listach).
  auto_line_items: RequisitionAutoLineItem[] | null;
}

export interface RequisitionAutoLineItem {
  id: string;
  name: string;
  trigger_names: string[];
  trigger_name: string | null;
  pricing_mode: "rate" | "lump_sum" | "time_based";
  pricing: string;
  quantity_rule: string;
  scope: string;
  will_be_added: boolean;
  skip_reason: string;
  // Dopisałaby się, ale zamawiający ją pominął (declined_auto_line_item_types).
  declined: boolean;
  // Pozycja jest już wierszem zapotrzebowania (kopiowana wprost), a nie dopisywana automatem.
  on_requisition: boolean;
}

export interface MinOrderAmountNotice {
  agreement_id: string;
  agreement_title: string;
  min_order_amount: string;
  total_value: string;
  below_minimum: boolean;
  shortfall: string;
}

export interface RequisitionWpwParcel {
  parcel_id: string;
  teryt: string;
  short_number: string;
  owners: {
    owner_id: string;
    full_name: string;
    files: ManagedFileBrief[];
  }[];
}

// Mirrors core.serializers.ManagedFileBriefSerializer — embedded attachment metadata.
export interface ManagedFileBrief {
  id: string;
  original_filename: string;
  content_type: string;
  size_bytes: number | null;
  url: string | null;
}

export interface OrderLineItem {
  id: string;
  line_item_type: string | null;
  line_item_type_name: string;
  line_item_type_unit: string;
  is_custom: boolean;
  custom_name: string;
  custom_unit: string;
  quantity: string;
  unit_rate: string;
  total_value: string;
  description: string;
  attachment_required: boolean;
  required_file_extension: string;
  attachment_count_per_unit: boolean;
  result_files_expected: boolean;
}

// --- Order result files (pliki pozycji realizacyjnej) ---

export type OrderResultFileStatus = "pending" | "accepted" | "rejected";

// Mirrors orders.serializers.OrderResultFileSerializer.
export interface OrderResultFile {
  id: string;
  line_item: string;
  original_name: string;
  status: OrderResultFileStatus;
  uploaded_by: string | null;
  uploaded_by_name: string;
  reviewed_by: string | null;
  reviewed_by_name: string;
  reviewed_at: string | null;
  review_comment: string;
  file_url: string | null;
  size_bytes: number | null;
  created_at: string;
  // --- Photo metadata (EXIF read at upload) + the screenshot verdict ---
  is_image: boolean;
  taken_at: string | null;
  gps_lat: number | null;
  gps_lng: number | null;
  has_geotag: boolean;
  camera_make: string;
  camera_model: string;
  software: string;
  image_width: number | null;
  image_height: number | null;
  // "" = not an image; "photo" | "suspect" (one criterion, reviewer's call) | "screenshot".
  photo_check: "" | "photo" | "suspect" | "screenshot";
  photo_check_reasons: string[];
  // Stamped by the automatic screenshot gate; never cleared (accept = "przywrócone przez X").
  auto_rejected_at: string | null;
  is_auto_rejected: boolean;
  was_restored: boolean;
}

// One flagged line item plus its uploaded result files (from the overview endpoint).
export interface OrderResultFileLineItem {
  id: string;
  name: string;
  unit: string;
  quantity: string;
  // LineItemType flags (photo checks): geotag expected / screenshots are a valid product.
  geotag_required: boolean;
  allow_screenshots: boolean;
  // Auto-rejected files the contractor has not replaced yet — blocks the POCK request.
  unresolved_auto_rejected_ids: string[];
  files: OrderResultFile[];
}

// Response of GET /orders/result-files/overview/?order=<id> — drives every UI surface.
/**
 * Konto portalu podwykonawcy z zamówienia + odpowiedź „czy TO konto wgra pliki".
 * `can_upload` w overview dotyczy PYTAJĄCEGO (z konta GK zawsze false), więc bez tej
 * listy widok zamawiającego nie umiał odpowiedzieć, dlaczego wykonawca nie przesyła.
 */
export interface OrderResultFilesContractorAccount {
  id: string;
  username: string;
  full_name: string;
  is_active: boolean;
  last_login: string | null;
  can_upload: boolean;
}

export interface OrderResultFilesOverview {
  order_id: string;
  order_number: string;
  can_manage: boolean;
  can_upload: boolean;
  /** Tylko dla odbierających pliki (can_manage); podwykonawca dostaje pustą listę. */
  contractor_accounts: OrderResultFilesContractorAccount[];
  line_items: OrderResultFileLineItem[];
}

export type OrderBillingSource = "requisition" | "designer_billing" | "group_designer_billing";

export interface OrderProjectBrief {
  id: string;
  name: string;
  // Czy przedsięwzięcie ma podpięty projekt ZE (DLC_01). Workspace ZE kluczuje po
  // id PRZEDSIĘWZIĘCIA, więc do linku wystarczy `id` powyżej + ta flaga.
  has_ze_project: boolean;
}

export type OrderAccessLevel = "contractor" | "full" | "manage" | "execute" | "view";

/** Rodzaj dostępu nadanego pracownikowi GK do jednego zamówienia (OrderAccessGrant). */
export type OrderAccessType = "view" | "manage" | "execute";

export const ORDER_ACCESS_TYPE_LABELS: Record<OrderAccessType, string> = {
  view: "Podgląd",
  manage: "Zarządzanie",
  execute: "Realizacja",
};

export const ORDER_ACCESS_TYPE_HINTS: Record<OrderAccessType, string> = {
  view: "Tylko podgląd karty zamówienia — bez kwot i bez żadnych akcji.",
  manage: "Opcje i widok takie same jak u zamawiającego. Powiadomienia o zamówieniu włącza sobie sam(a) przyciskiem „Włącz powiadomienia” na karcie — osobno dla każdej osoby.",
  execute: "Widok jak u podwykonawcy, ale bez kwot, bez wniosków o zmianę zamówienia i bez wniosków o POCK. Wykonane prace przekazuje przez POCK bez kwot (pozycje, procent, załączniki), z którego nie wystawia się faktury.",
};

/** Pracownik GK z dostępem innym niż „Podgląd” — kontakt pokazywany pod zamawiającym. */
export interface OrderGrantedStaff {
  id: string;
  full_name: string;
  access_type: Exclude<OrderAccessType, "view">;
  access_type_label: string;
  phone: string | null;
  email: string | null;
}

export interface OrderAccessGrant {
  id: string;
  order: string;
  user: string;
  user_name: string;
  user_role: string;
  user_role_display: string;
  access_type: OrderAccessType;
  access_type_display: string;
  granted_by: string | null;
  granted_by_name: string | null;
  created_at: string;
  updated_at: string;
}

/** Pracownik, któremu da się jeszcze nadać dostęp (bez ról widzących wszystko). */
export interface OrderAccessCandidate {
  id: string;
  full_name: string;
  role: string;
  role_display: string;
  email: string;
  job_title: string;
}

export interface OrderAccessGrantsResponse {
  grants: OrderAccessGrant[];
  candidates: OrderAccessCandidate[];
}

export interface Order {
  id: string;
  order_number: string;
  requisition: string;
  billing_source: OrderBillingSource;
  // Przedsięwzięcie(s) the order concerns — null/empty for the contractor (hidden).
  // `projects` holds all of them; a GROUP_DESIGNER_BILLING order has several.
  project_id: string | null;
  project_name: string | null;
  projects: OrderProjectBrief[];
  // Skrót dla zwykłego zamówienia: przedsięwzięcie ma projekt ZE (DLC_01).
  // Zamówienie grupowe → sprawdzaj per pozycja w `projects`. Fałsz u podwykonawcy.
  has_ze_project: boolean;
  // Kwota minimalna zamówienia z umowy ramowej (jak na zapotrzebowaniu) — po stawkach
  // zapisanych na pozycjach zamówienia. Brak w podglądzie „jak widzi podwykonawca".
  min_order_amount_notice?: MinOrderAmountNotice | null;
  project_company_id: string | null;
  project_company_short_code: string | null;
  project_company_name: string | null;
  approved_by: string;
  approved_by_name: string;
  // Zamawiający (osoba odpowiedzialna GK) — domyślnie autor zapotrzebowania, edytowalny przez
  // dyrektora technicznego. Telefon i e-mail pochodzą z profilu tej osoby (zmieniają się z nią).
  ordered_by: string | null;
  ordered_by_name: string | null;
  ordered_by_phone: string | null;
  ordered_by_email: string | null;
  contractor: string;
  contractor_name: string;
  // Kontakt do firmy podwykonawcy — z jego kartoteki (Podwykonawcy → dane kontaktowe).
  contractor_contact_person: string;
  contractor_contact_phone: string;
  contractor_contact_email: string;
  // Konkretna osoba u podwykonawcy, która przyjęła zlecenie. Null, dopóki nikt z firmy
  // nic nie potwierdził (m.in. przyjęcie wymuszone przez dyrekcję). Telefon i e-mail
  // pochodzą z profilu tej osoby, jak przy zamawiającym.
  accepted_by: string | null;
  accepted_by_name: string | null;
  accepted_by_phone: string | null;
  accepted_by_email: string | null;
  accepted_at: string | null;
  status: OrderStatus;
  order_type: OrderType;
  order_type_display: string;
  approved_at: string;
  deadline: string | null;
  /** Termin przyjęcia — dzień, do którego podwykonawca powinien przyjąć/odrzucić zlecenie
   *  (data wysłania + termin z umowy ramowej, domyślnie 5 dni; zamawiający może przedłużyć).
   *  Po nim nieprzyjęte zamówienie dostaje flagę `acceptance_overdue_since` (nie jest anulowane). */
  confirmation_deadline: string | null;
  /** Kiedy zamówienie ostatnio trafiło do podwykonawcy jako „Wysłane" (null = starsze). */
  sent_at?: string | null;
  /** Termin bazowy na przyjęcie (dni) zapisany przy wysłaniu i jego źródło. */
  acceptance_deadline_days?: number | null;
  acceptance_deadline_source?: AcceptanceDeadlineSource | "";
  /** Czy zamawiający przedłużył termin przyjęcia ponad termin bazowy. */
  acceptance_deadline_extended?: boolean;
  /** Czy termin pilnuje automat (false poza „Wysłane" i przy auto-akceptacji). */
  acceptance_deadline_tracked?: boolean;
  /** Flaga „długo oczekuje na przyjęcie" — od kiedy zamówienie stoi po terminie. Zamówienie
   *  nadal czeka na podwykonawcę; dyrektorzy widzą je w badge'u i decydują o anulowaniu. */
  acceptance_overdue_since?: string | null;
  priority: Priority;
  description: string;
  closed_at: string | null;
  closed_reason?: string;
  closed_by_name?: string | null;
  rejection_comment: string;
  contractor_acknowledged: boolean;
  pending_change_requests_count: number;
  pending_pock_requests_count: number;
  allow_partial_pock: boolean;
  /** Why the contractor cannot submit a POCK request right now — null when they can.
   *  Same text the API refuses with; the portal shows it next to the button. */
  pock_request_blocked_reason: string | null;
  allow_external_costs: boolean;
  contractor_fee_requests_allowed: boolean;
  has_finance_access: boolean;
  is_assigned_coordinator: boolean;
  // ── Dostęp ZALOGOWANEJ osoby do tego zamówienia (backend: apps.orders.access) ──
  // `access_level`: contractor | full (role widzące wszystko) | manage (koordynator
  // przedsięwzięcia, zamawiający, grant „Zarządzanie") | execute | view.
  // `access_via_grant`: jedyną drogą do zamówienia jest nadany dostęp (OrderAccessGrant)
  // — para z `access_level` wybiera wariant karty (podgląd bez akcji / karta GK /
  // widok jak u podwykonawcy). `amounts_hidden`: kwoty w odpowiedzi są zamaskowane.
  access_level: OrderAccessLevel | null;
  access_via_grant: boolean;
  amounts_hidden: boolean;
  /** Akcje jak u zamawiającego (kamienie, wnioski, koszty, raporty, odbiór plików). */
  can_manage: boolean;
  /** Może nadawać dostęp pracownikom GK (zamawiający / dyrektor techniczny / prezes). */
  can_grant_access: boolean;
  /** Pracownicy GK z dostępem „Zarządzanie”/„Realizacja” — kontakty pod zamawiającym. */
  granted_staff: OrderGrantedStaff[];
  /** Zakończone końcowym POCK bez kwot i bez nierozliczonych protokołów z kwotami —
   *  nie ma czego fakturować, więc zamknąć może prowadzący / realizujący, nie tylko dyrektor. */
  finished_without_settlement: boolean;
  unread_comments_count: number;
  requires_weekly_reports: boolean | null;
  report_day_of_week: number | null;
  report_due_time: string | null;
  report_template: string | null;
  effective_requires_weekly_reports: boolean;
  effective_report_day_of_week: number | null;
  effective_report_due_time: string | null;
  effective_report_template: string;
  line_items: OrderLineItem[];
  milestones: OrderMilestone[];
  milestone_events: OrderMilestoneEvent[];
  files_detail: ManagedFileBrief[];
  // Wykaz właścicieli do pozyskania zgód (XLSX) — tylko zamówienia WPW; null dopóki
  // zadanie w tle go nie zbuduje (po utworzeniu zamówienia / zmianie zakresu).
  // Nazwa pól pochodzi z API i zostaje; w interfejsie nazywamy to wykazem właścicieli.
  mml_file_detail: ManagedFileBrief | null;
  mml_file_generated_at: string | null;
  // Czy zamówienie ma udostępnioną zakładkę „Pliki" projektu ZE (sekcja „Pliki z projektu
  // ZE" — żywa projekcja; zawartość zwraca GET /orders/{id}/project-files/).
  has_project_files_share: boolean;
  // Miejscowości, których zamówienie dotyczy — z działek zamówienia, a gdy ich nie ma,
  // z działek przedsięwzięcia ZE (`localities_source`). Widoczne też dla podwykonawcy.
  localities: string[];
  localities_source: "parcels" | "project" | "";
  // Czy zalogowana osoba włączyła sobie powiadomienia o tym zamówieniu (komentarze,
  // zdarzenia, pliki wgrane przez podwykonawcę). Tylko strona GK — u podwykonawcy zawsze false.
  is_subscribed: boolean;
  // Znacznik „coś nowego od mojego ostatniego wejścia" (nowa pozycja, plik, komentarz…).
  has_new_activity: boolean;
  last_activity_at: string | null;
  last_activity_kind: string;
  last_activity_label: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

// Drzewo udostępnionych folderów projektu ZE w zamówieniu — kształt węzłów jak w zakładce
// „Pliki" projektu (power_projects.FolderNode), zawężony do udostępnionych, niepoufnych.
export interface OrderProjectFileEntry {
  managed_file_id: string;
  filename: string;
  url: string | null;
  size_bytes: number | null;
  created_at: string | null;
  source_label: string;
}

export interface OrderProjectFolderNode {
  slug: string;
  label: string;
  source_hint: string;
  file_count: number; // rekurencyjny
  entries: OrderProjectFileEntry[];
  children: OrderProjectFolderNode[];
}

export interface OrderProjectFilesResponse {
  shared: boolean;
  folders: OrderProjectFolderNode[];
}

// Autowiadomość audytowa dla kamieni milowych — kto/kiedy/co.
export type OrderMilestoneActionKind = "created" | "completed" | "reopened" | "edited" | "deleted";
export interface OrderMilestoneEvent {
  id: string;
  action: OrderMilestoneActionKind;
  milestone_label: string;
  detail: string;
  actor_name: string;
  message: string;
  created_at: string;
}

// Historia zdarzeń zamówienia — ujednolicony audyt wszystkich akcji koordynatora
// i podwykonawcy (GET /orders/{id}/events/, tylko role wewnętrzne).
export type OrderAuditEventKind =
  | "created"
  | "status"
  | "acknowledgement"
  | "milestone"
  | "attachment"
  | "result_file"
  | "comment"
  | "change_request"
  | "external_cost"
  | "weekly_report"
  | "penalty"
  | "pock"
  | "mail"
  | "edit"
  | "settings"
  | "other";

export interface OrderAuditEvent {
  id: string;
  kind: OrderAuditEventKind;
  kind_display: string;
  from_status: string;
  from_status_display: string;
  to_status: string;
  to_status_display: string;
  description: string;
  actor_name: string;
  meta: Record<string, unknown>;
  created_at: string;
}

// Kamień milowy zamówienia — termin pośredni "nazwa → data" (+ opcjonalne wykonanie).
// `code` set → predefiniowany checkpoint ZE (pryncypialny, nieusuwalny).
export interface OrderMilestone {
  id: string;
  label: string;
  code: string;
  due_date: string;
  completed_at: string | null;
  sequence: number;
  is_principal: boolean;
  is_done: boolean;
  // Kto oznaczył kamień jako wykonany (z audytu kamienia); „automatycznie" przy sprzężeniu
  // z projektem technicznym; null = kamień otwarty albo bez śladu.
  completed_by_name?: string | null;
}

export type WeeklyReportStatus = "pending" | "submitted" | "late" | "accepted" | "rejected";

export const WEEKLY_REPORT_STATUS_LABELS: Record<WeeklyReportStatus, string> = {
  pending: "Oczekujący",
  submitted: "Złożony",
  late: "Po terminie",
  accepted: "Zaakceptowany",
  rejected: "Odrzucony",
};

export interface WeeklyReportFile {
  id: string;
  file: string;
  file_url: string | null;
  original_name: string;
  uploaded_by: string | null;
  uploaded_by_name: string | null;
  created_at: string;
}

export interface WeeklyReport {
  id: string;
  order: string;
  order_number: string;
  due_date: string;
  period_start: string;
  period_end: string;
  submitted_at: string | null;
  status: WeeklyReportStatus;
  status_display: string;
  director_comment: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  reviewed_by_name: string | null;
  director_read: boolean;
  files: WeeklyReportFile[];
  created_at: string;
  updated_at: string;
}

export interface PenaltyCalcTerm {
  term_id: string;
  description: string;
  base_value: string;
  penalty_per_day_pct: string | null;
  penalty_per_day_amount: string | null;
  max_penalty_pct: string | null;
  max_penalty_amount: string | null;
  calculated_amount: string;
  termination_possible: boolean;
  termination_after_days: number | null;
}

export interface OverduePenaltyTerm {
  term_id: string;
  description: string;
  base_value: string;
  is_delay_penalty: boolean;
  daily_amount: string | null;
  flat_penalty_amount: string | null;
  calculated_amount: string;
  termination_possible: boolean;
  termination_after_days: number | null;
  // for one-time terms:
  already_applied: boolean;
  applied_record_id: string | null;
}

export interface OverduePenaltyRow {
  order_id: string;
  order_number: string;
  contractor_name: string;
  project_name: string;
  project_id: string;
  deadline: string;
  days_overdue: number;
  order_value: string;
  agreement_id: string | null;
  agreement_title: string | null;
  daily_penalty_forgiven: boolean;
  daily_penalty_forgiven_note: string;
  accumulated_daily_amount: string;
  settled_amount: string;
  terms: OverduePenaltyTerm[];
  total_calculated_penalty: string;
  termination_possible: boolean;
}

export interface AppliedPenaltyRecord {
  id: string;
  description: string;
  amount: string;
  note: string;
  is_termination: boolean;
  is_forgiven: boolean;
  is_settled: boolean;
  settlement_note: string;
  settled_at: string | null;
  created_at: string;
}

export interface AppliedPenalties {
  daily_penalty_forgiven: boolean;
  daily_penalty_forgiven_note: string;
  accumulated_daily_amount: string;
  settled_amount: string;
  onetime_records: AppliedPenaltyRecord[];
}

export interface PenaltyCalc {
  days_overdue: number;
  deadline: string | null;
  agreement_id: string | null;
  agreement_title: string | null;
  order_value: string;
  terms: PenaltyCalcTerm[];
  total_calculated_penalty: string;
  termination_possible: boolean;
}

export type PockRequestStatus = "pending" | "approved" | "rejected";

export const POCK_REQUEST_STATUS_LABELS: Record<PockRequestStatus, string> = {
  pending: "Oczekuje",
  approved: "Zatwierdzone",
  rejected: "Odrzucone",
};

export interface PockRequestItemAttachment {
  id: string;
  managed_file: string;
  managed_file_detail: ManagedFileBrief | null;
  original_name: string;
  created_at: string;
}

export interface PockRequestItem {
  id: string;
  order_line_item: string;
  order_line_item_name: string;
  order_line_item_unit: string;
  // Snapshot of the order line AS SUBMITTED — a later change request does not
  // rewrite these (nor amount_net). live_* is the current line, for comparison.
  order_line_item_quantity: string;
  // null when the viewer may not see rates (rate_visibility — same matrix as the order)
  order_line_item_unit_rate: string | null;
  live_quantity: string;
  live_unit_rate: string | null;
  /** What the contractor typed; the percentage is derived from it. */
  accepted_quantity: string | null;
  completion_percentage: string;
  is_stale: boolean;
  amount_net: string | null;
  attachments: PockRequestItemAttachment[];
}

export interface PockRequest {
  id: string;
  order: string;
  order_number: string;
  contractor_name: string;
  project_name: string;
  requested_by: string;
  requested_by_name: string;
  status: PockRequestStatus;
  note: string;
  review_comment: string;
  is_withdrawn: boolean;
  proposed_settlement_pct: string;
  approved_settlement_pct: string | null;
  /** The contractor asks for the FINAL POCK — the default GK may change when approving. */
  proposed_is_final: boolean;
  protocol_id: string | null;
  protocol_withdrawn: boolean;
  /** POCK rozliczony poza obiegiem (archiwum) — podwykonawca takiego wniosku nie dostaje. */
  protocol_archived: boolean;
  // null when any item's rate is hidden from the viewer (a partial total would understate)
  total_amount_net: string | null;
  /** The order changed after submission — approval is refused with stale_reason. */
  is_stale: boolean;
  stale_reason: string;
  items: PockRequestItem[];
  /** Files of the whole request (not of one line) — copied onto the POCK on approval. */
  document_attachments?: DocumentAttachment[];
  created_at: string;
  updated_at: string;
}

export const PRIORITY_LABELS: Record<Priority, string> = {
  low: "Niski",
  standard: "Standardowy",
  high: "Wysoki",
};

export const REQUISITION_STATUS_LABELS: Record<RequisitionStatus, string> = {
  draft: "Szkic",
  submitted: "Zgłoszone",
  approved: "Zatwierdzone",
  rejected: "Odrzucone",
};

// Zamówienie bez terminów do pilnowania: odebrane końcowo, zamknięte, anulowane.
// Lustro apps/orders/models.FINISHED_ORDER_STATUSES — m.in. blokada dodawania
// kamieni milowych (backend odpowiada wtedy 400).
export const FINISHED_ORDER_STATUSES: readonly OrderStatus[] = ["accepted_final", "closed", "cancelled"];

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  sent: "Wysłane",
  accepted: "Przyjęte",
  rejected: "Odrzucone",
  in_progress: "W realizacji",
  in_progress_partial: "W realizacji (częściowo odebrane)",
  submitted_for_acceptance: "Zgłoszone do odbioru końcowego",
  accepted_final: "Zakończone",
  closed: "Zamknięte",
  cancelled: "Anulowane",
};

// Short forms for badges and the list filter; the full backend wording comes with each
// order as `order_type_display` (badge tooltip).
export const ORDER_TYPE_LABELS: Record<OrderType, string> = {
  standard: "Standardowe",
  wpw_terenowe: "WPW terenowe",
  wpw_kompleksowe: "WPW kompleksowe",
  ze_opracowanie: "Opracowanie ZE",
  ze_kosztorys: "Kosztorys ZE",
  mdcp_geodezja: "MDCP (geodezja)",
  wody_polskie: "Wody Polskie",
  project_lead: "Prowadzenie zlecenia",
};

// Types a director may pick by hand when approving a requisition. The design-stage kinds
// come from their stage link and project_lead only from the hand-over dialog. The two WPW
// kinds replace the old „Negocjator WPW" option (backend migrations 0024 / 0059).
export const MANUAL_ORDER_TYPE_OPTIONS: { value: OrderType; label: string }[] = [
  { value: "standard", label: "Standardowe" },
  { value: "wpw_terenowe", label: "Pozyskanie zgód WPW (terenowe)" },
  { value: "wpw_kompleksowe", label: "Pozyskanie zgód WPW (kompleksowe, art.124)" },
];

// ── OrderComment──────────────────────────────────────────────────────────────

export type OrderCommentStatus = "posted" | "acknowledged" | "resolved";

export interface OrderComment {
  id: string;
  order: string;
  author: string;
  author_name: string;
  author_role: string;
  body: string;
  is_critical: boolean;
  status: OrderCommentStatus;
  status_display: string;
  response: string;
  acknowledged_by: string | null;
  acknowledged_by_name: string | null;
  acknowledged_at: string | null;
  resolved_by: string | null;
  resolved_by_name: string | null;
  resolved_at: string | null;
  files_detail: ManagedFileBrief[];
  created_at: string;
  updated_at: string;
}

// ── ExternalCostRequest ───────────────────────────────────────────────────────

export type ExternalCostStatus = "pending" | "approved" | "rejected" | "incurred" | "settled";
export type ExternalCostType = "admin" | "notarial" | "court" | "map" | "operat" | "other";

export const EXTERNAL_COST_TYPE_LABELS: Record<ExternalCostType, string> = {
  admin: "Opłata administracyjna",
  notarial: "Opłata notarialna",
  court: "Opłata sądowa",
  map: "Mapa / wyrys / wypis",
  operat: "Operat / opinia techniczna",
  other: "Inne",
};

export const EXTERNAL_COST_STATUS_LABELS: Record<ExternalCostStatus, string> = {
  pending: "Oczekuje na decyzję",
  approved: "Zatwierdzone",
  rejected: "Odrzucone",
  incurred: "Poniesione",
  settled: "Rozliczone",
};

export interface ExternalCostRequest {
  id: string;
  order: string;
  requested_by: string;
  requested_by_name: string;
  cost_type: ExternalCostType;
  cost_type_display: string;
  description: string;
  amount_estimated: string;
  justification: string;
  status: ExternalCostStatus;
  status_display: string;
  approved_by: string | null;
  approved_by_name: string | null;
  approved_at: string | null;
  approved_amount: string | null;
  approval_note: string;
  rejection_comment: string;
  amount_actual: string | null;
  invoice_managed_file: string | null;
  invoice_managed_file_detail: { id: string; original_filename: string; content_type: string; size_bytes: number; url: string | null } | null;
  invoice_file_url: string | null;
  settlement_method: string;
  settled_by: string | null;
  settled_by_name: string | null;
  settled_at: string | null;
  settlement_note: string;
  created_at: string;
  updated_at: string;
}

export type OrderChangeRequestStatus = "pending" | "approved" | "rejected" | "withdrawn";
export type OrderChangeRequestType = "contractor" | "gk_with_approval" | "gk_notification_only";

export const ORDER_CHANGE_REQUEST_STATUS_LABELS: Record<OrderChangeRequestStatus, string> = {
  pending: "Oczekuje",
  approved: "Zatwierdzony",
  rejected: "Odrzucony",
  withdrawn: "Wycofany",
};

export const ORDER_CHANGE_REQUEST_TYPE_LABELS: Record<OrderChangeRequestType, string> = {
  contractor: "Wniosek Podwykonawcy",
  gk_with_approval: "Zmiana GK (z prośbą o akceptację)",
  gk_notification_only: "Zmiana GK (tylko powiadomienie)",
};

export interface LineItemChange {
  order_line_item_id: string | null;  // null = new item to be added
  line_item_type_id?: string;         // required when order_line_item_id is null
  line_item_type_name: string;
  line_item_type_unit: string;
  current_quantity: string;
  current_unit_rate: string;
  new_quantity: string;
  new_unit_rate: string;
}

export interface AvailableLineItemType {
  id: string;
  name: string;
  unit: string;
  base_rate: string;
  agreement_title: string;
}

// Zmiana listy WPW zapamiętana przy wniosku (wysyłana przez GK). `files` = plik(i) ze
// zgodą wgrane przez POST /files/ — puste przy "add" = skompilowany pakiet WPW właściciela.
// add = nowy WPW; remove = WPW wypada z zamówienia; replace = zamiana pakietu TEGO SAMEGO
// właściciela (nowy plik ze zgodą dla negocjatora, liczba WPW bez zmian).
export type WpwOwnerChangeAction = "add" | "remove" | "replace";

export interface WpwOwnerChangeInput {
  owner: string;
  action: WpwOwnerChangeAction;
  files?: string[];
}

// Wiersz zapamiętanej listy (backend zwraca go tylko użytkownikom wewnętrznym —
// podwykonawca dostaje pustą listę, to nie jest część wniosku).
export interface WpwOwnerChangeRow {
  owner_id: string;
  owner_name: string;
  parcel_teryt: string;
  parcel_number: string;
  action: WpwOwnerChangeAction;
  files: { id: string; name: string }[];
}

export interface WpwPackageBrief {
  number: string;
  file_name: string | null;
  usable: boolean;
  reason: string;
}

// GET /orders/:id/wpw-scope/ — dane formularza zmiany listy WPW.
export interface WpwScope {
  count_line_item: { id: string; name: string; unit: string; quantity: string } | null;
  handovers: {
    handover_id: string;
    owner_id: string;
    owner_name: string;
    parcel_id: string;
    parcel_teryt: string;
    parcel_number: string;
    declared_status: string;
    declared_status_display: string;
    active_files: number;
    block_reason: string;
    // zgoda już zebrana → pakietu się nie zamienia ('' = można)
    replace_block_reason: string;
    in_pending_request: boolean;
    // najnowszy pakiet właściciela („zamień pakiet”) i czy negocjator już ma dokładnie ten plik
    package: WpwPackageBrief | null;
    package_handed: boolean;
  }[];
  candidates: {
    owner_id: string;
    owner_name: string;
    ownership_share: string;
    parcel_id: string;
    parcel_teryt: string;
    parcel_number: string;
    in_field: boolean;
    in_pending_request: boolean;
    package: WpwPackageBrief | null;
    // właściciel JUŻ w zamówieniu z nowym pakietem: zaznaczony razem ze swoim starym WPW
    // w „do usunięcia” = zamiana pakietu (ilość bez zmian); sam — odmowa
    in_order: boolean;
  }[];
}

export interface OrderChangeRequest {
  id: string;
  order: string;
  order_number: string;
  requested_by: string;
  requested_by_name: string;
  change_type: OrderChangeRequestType;
  change_type_display: string;
  description: string;
  new_deadline: string | null;
  new_description: string;
  line_item_changes: LineItemChange[];
  wpw_owner_changes?: WpwOwnerChangeRow[];
  status: OrderChangeRequestStatus;
  status_display: string;
  reviewed_by: string | null;
  reviewed_by_name: string | null;
  review_comment: string;
  created_at: string;
  updated_at: string;
}

// Meta „widoku podwykonawcy" (GET /orders/:id/contractor-preview/) — czyim okiem
// patrzymy i jaki jest domyślny wariant widoczności stawek (wg grantów kont firmy).
export interface ContractorPreviewMeta {
  contractor_name: string;
  can_see_rates: boolean;
  accounts_total: number;
  accounts_with_rates: number;
}

// „Moje terminy" podwykonawcy (GET /orders/contractor-todo/ i podgląd GK
// /orders/contractor-todo-preview/?contractor=). Pozycje mają WYŁĄCZNIE pola
// z białej listy backendu (apps/orders/contractor_todo.py) — bez przedsięwzięć,
// kwot i historii kamieni.
export interface ContractorTodoItem {
  kind: "milestone" | "order_deadline";
  id: string;
  label: string;
  code: string;
  due_date: string;
  is_done: boolean;
  completed_at: string | null;
  days_left: number | null;
  order_id: string;
  order_number: string;
  order_status: OrderStatus;
  localities: string[];
  // Cofnięcie wykonania przez zlecającego (ostatnie zdarzenie kamienia); null
  // dla terminów końcowych, kamieni wykonanych i nigdy niecofniętych.
  reopened_at: string | null;
}

export interface ContractorTodoData {
  items: ContractorTodoItem[];
  today: string;
  urgent_window_days: number;
  counts: { overdue: number; urgent: number; upcoming: number; done: number };
}

export interface ContractorTodoPreviewMeta {
  contractor_id: string;
  contractor_name: string;
}

// Mirrors orders.serializers.OrderMailLogSerializer. Internal-only — a contractor
// account gets 403 on the endpoint, so this never reaches the contractor UI.
export type OrderMailKind = "created" | "changed";

export type OrderMailStatus =
  | "pending"
  | "sent"
  | "failed"
  | "blocked_foreign_recipient"
  | "skipped_no_recipients"
  | "skipped_contractor_off"
  | "skipped_disabled";

export interface OrderMailLog {
  id: string;
  kind: OrderMailKind;
  kind_display: string;
  status: OrderMailStatus;
  status_display: string;
  to: string[];
  cc: string[];
  reply_to: string;
  subject: string;
  body_preview: string;
  /** Pełna treść wiadomości (odtworzona z zamówienia) — do rozwijanego podglądu. */
  body: string;
  changes: string[];
  error: string;
  attempts: number;
  last_attempt_at: string | null;
  sent_at: string | null;
  created_at: string;
  actor_name: string;
  can_retry: boolean;
}
